import React, { useState } from 'react';
import { Box, Modal, Typography, Button } from '@mui/material';
import { Share } from '@mui/icons-material';
import PropTypes from 'prop-types';
import ActionButton from './ActionButton.jsx';
import {
    SHARE_NETWORKS,
    SITE_URL,
    canWebShareFiles,
    isMobileShareDevice,
    buildTravelCaption,
    buildShaperCaption,
    buildTrailCaption,
    buildRoadTripCaption,
    flattenForShare,
} from '../utils/share';
import { createSharePage } from '../utils/sharePage';
import { trackShare } from '../utils/analytics';
import { trackFbShare } from '../utils/facebookPixel';
import { trackPinShare } from '../utils/pinterestTag';
import { PALETTE } from '../theme';

const trackAll = (network, trackId, gaParams) => {
    trackShare({ network, ...gaParams });
    trackFbShare(trackId, network);
    trackPinShare(trackId, network);
};

/**
 * Reusable "Share to social media" control for every generated map.
 *
 * An image can only land in a social post as a *file* — no link/URL share can
 * inject one. So:
 *   - phones/tablets: the native share sheet carries the real image file into
 *     Instagram, WhatsApp, etc. in one tap;
 *   - desktop: a dialog whose primary action is "Download image" (attach it to
 *     the post yourself), plus link buttons for link-preview-card sharing.
 */
const ShareControl = ({
    kind,
    region,
    visited,
    total,
    label,
    shapeName,
    trailTitle,
    trailDistance,
    trailDays,
    roadtripTitle,
    roadtripStops,
    roadtripDistance,
    getBlob,
    fileName,
    trackId,
    disabled = false,
    variant = 'secondary',
    sx,
}) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [shareData, setShareData] = useState(null);
    const [error, setError] = useState(false);

    const buildText = () => {
        if (kind === 'shaper') return buildShaperCaption({ shapeName });
        if (kind === 'trail') return buildTrailCaption({ title: trailTitle, distance: trailDistance, days: trailDays });
        if (kind === 'roadtrip') return buildRoadTripCaption({ title: roadtripTitle, stops: roadtripStops, distance: roadtripDistance });
        return buildTravelCaption({ region, visited, total, label });
    };

    const gaParams = kind === 'shaper'
        ? { kind: 'shaper', shape: shapeName }
        : kind === 'trail'
            ? { kind: 'trail', mapType: trackId }
            : kind === 'roadtrip'
                ? { kind: 'roadtrip', mapType: trackId }
                : { kind: 'travel', mapType: trackId, visitedCount: visited, totalCount: total };

    const handleClick = async () => {
        setBusy(true);
        setShareData(null);
        setError(false);
        try {
            const { title, caption } = buildText();
            const imageBlob = await getBlob();

            // Phones/tablets: the native share sheet sends the real image file
            // straight into Instagram, WhatsApp, Messages, etc.
            if (isMobileShareDevice()) {
                const file = new File([imageBlob], fileName, {
                    type: imageBlob.type || 'image/png',
                });
                if (canWebShareFiles([file])) {
                    try {
                        await navigator.share({ files: [file], text: caption });
                        trackAll('native', trackId, gaParams);
                        return;
                    } catch (err) {
                        if (err && err.name === 'AbortError') return;
                    }
                }
            }

            // Desktop: open the dialog. Host the image + a share page so the
            // link buttons still get a preview card.
            //
            // The on-canvas blob is a transparent PNG (so the user's download
            // and the product print look right on dark cups/t-shirts). Social
            // crawlers (Bluesky, X, Reddit) don't reliably render transparent
            // PNGs as og:image, so we flatten onto white + re-encode as JPEG
            // specifically for the share-page upload.
            const flatShareBlob = await flattenForShare(imageBlob).catch((err) => {
                // If flattening fails for any reason, fall back to the
                // original blob rather than aborting the share entirely.
                console.warn('Share flatten failed, posting original blob:', err);
                return imageBlob;
            });
            const search = typeof window !== 'undefined' ? window.location.search : '';
            const { pageUrl } = await createSharePage({
                blob: flatShareBlob,
                title,
                targetUrl: `${SITE_URL}/${search}`,
            });
            setShareData({ pageUrl, caption });
            setModalOpen(true);
        } catch (err) {
            console.error('Share failed:', err);
            setError(true);
            setModalOpen(true);
        } finally {
            setBusy(false);
        }
    };

    const openNetwork = (network) => {
        if (!shareData) return;
        window.open(
            network.buildUrl(shareData),
            '_blank',
            'noopener,noreferrer,width=600,height=560'
        );
        trackAll(network.id, trackId, gaParams);
    };

    return (
        <>
            <ActionButton
                variant={variant}
                startIcon={<Share />}
                onClick={handleClick}
                disabled={disabled}
                loading={busy}
                sx={sx}
            >
                Share
            </ActionButton>

            <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 460,
                        maxWidth: '92vw',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        backgroundColor: 'rgba(255, 255, 255, 0.98)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 3,
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                        p: 4,
                    }}
                >
                    <Typography
                        variant="h5"
                        component="h2"
                        sx={{ fontWeight: 'bold', textAlign: 'center', mb: 0.5 }}
                    >
                        Share your map
                    </Typography>

                    {error ? (
                        <Typography
                            variant="body1"
                            sx={{ textAlign: 'center', color: '#c0392b', mt: 2, mb: 1 }}
                        >
                            Something went wrong preparing your share. Please try again.
                        </Typography>
                    ) : (
                        <>
                            <Typography
                                variant="body2"
                                sx={{ textAlign: 'center', color: '#666', mb: 2 }}
                            >

                            </Typography>

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                    justifyContent: 'center',
                                }}
                            >
                                {SHARE_NETWORKS.map((n) => (
                                    <Button
                                        key={n.id}
                                        onClick={() => openNetwork(n)}
                                        sx={{
                                            backgroundColor: n.color,
                                            color: '#ffffff',
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            borderRadius: 2,
                                            px: 2,
                                            py: 1,
                                            minWidth: 128,
                                            '&:hover': {
                                                backgroundColor: n.color,
                                                filter: 'brightness(0.92)',
                                            },
                                        }}
                                    >
                                        {n.label}
                                    </Button>
                                ))}
                            </Box>
                        </>
                    )}

                    <Box sx={{ textAlign: 'center', mt: 2.5 }}>
                        <Button
                            variant="text"
                            onClick={() => setModalOpen(false)}
                            sx={{ color: PALETTE.textMuted, textTransform: 'none' }}
                        >
                            Close
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

ShareControl.propTypes = {
    kind: PropTypes.oneOf(['travel', 'shaper', 'trail', 'roadtrip']).isRequired,
    region: PropTypes.string,
    visited: PropTypes.number,
    total: PropTypes.number,
    label: PropTypes.string,
    shapeName: PropTypes.string,
    trailTitle: PropTypes.string,
    trailDistance: PropTypes.string,
    trailDays: PropTypes.number,
    roadtripTitle: PropTypes.string,
    roadtripStops: PropTypes.number,
    roadtripDistance: PropTypes.string,
    getBlob: PropTypes.func.isRequired,
    fileName: PropTypes.string.isRequired,
    trackId: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    variant: PropTypes.oneOf(['primary', 'secondary', 'dark', 'ghost']),
    sx: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default ShareControl;
